import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAPI } from "../../contexts/APIContext";

/**
 * AuthGuard component manages authentication-based navigation
 * to prevent the "jumpy" redirects when authentication state changes
 */
export default function AuthGuard({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAPI();
  const location = useLocation();
  const navigate = useNavigate();
  const [prevAuthState, setPrevAuthState] = useState<{
    isAuthenticated: boolean;
    path: string;
    isLoading: boolean;
  }>({
    isAuthenticated: !!user,
    path: location.pathname,
    isLoading: loading,
  });
  const [renderContent, setRenderContent] = useState(false);

  useEffect(() => {
    // Don't do anything while loading
    if (loading) return;

    const isAuthenticated = !!user;
    const currentPath = location.pathname;
    const needsOnboarding = user && (!user.name || user.interests.length <= 0);

    // Check if auth state changed
    if (prevAuthState.isAuthenticated !== isAuthenticated || prevAuthState.isLoading !== loading) {
      // If user logged in
      if (isAuthenticated) {
        // If user needs onboarding, redirect to onboarding flow first
        if (needsOnboarding && currentPath !== "/onboarding") {
          navigate("/onboarding", { replace: true });
        }
        // Special case for login page - redirect to dashboard only if user has completed onboarding
        else if (!needsOnboarding && currentPath === "/login") {
          navigate("/dashboard", { replace: true });
        }
      }

      setPrevAuthState({ isAuthenticated, path: currentPath, isLoading: loading });
    }

    // Set renderContent to true once we've handled redirects
    if (!renderContent) {
      setRenderContent(true);
    }
  }, [user, loading, location.pathname, prevAuthState, renderContent, navigate]);

  // Show loading screen while handling redirects
  if (loading || !renderContent) {
    return (
      <div className="min-h-screen flex justify-center items-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return <>{children}</>;
}
